import { Camera, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Avatar, PICTURE_BUDGET } from "@/components/avatar";
import { FilePreview } from "@/components/file-preview";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { cn } from "@/utils/utils";

/**
 * The largest square that gets stored, and what it is for.
 *
 * This was 160 — the size the detail page happens to draw the avatar at — and
 * so every picture replaced through this dialog was stored at a sixth of the
 * resolution the enrolment form stores one at (`readPicture`, 1400px on the
 * long side). On any retina screen that is already a 2x upscale of a 160px
 * JPEG, and opened at full size it is unreadable. A member's photograph is
 * looked at to recognise a face; 800 is what the app's other image fields use
 * (`readImage(file, 800)`) and it costs about a tenth of the column budget.
 */
const MAX_SIZE = 800;
/**
 * The smallest we will settle for when the source cannot fill `MAX_SIZE`.
 *
 * A framing tighter than this is somebody zoomed a long way into a small file;
 * the square is exported at whatever the source actually holds rather than
 * upscaled, and this is the floor under that.
 */
const MIN_SIZE = 160;
/** The crop stage's viewport, in CSS pixels. */
const STAGE = 288;

/**
 * How the crop is asked to encode, in order, until one fits the column.
 *
 * The same ladder `readPicture` climbs, and for the same reason: the picture
 * is stored as a data URL in a column that refuses more than 400,000
 * characters, and a refusal after the crop was framed is a refusal nobody can
 * act on. The first rendering is what almost every picture takes.
 */
const RENDERINGS = [
  [1, 0.85],
  [1, 0.72],
  [0.75, 0.68],
  [0.5, 0.62],
] as const;

/** Inside somebody else's page, where the camera is theirs to lend. */
const inAFrame = () => typeof window !== "undefined" && window.self !== window.top;

/**
 * Offered wherever the browser has a camera API and a secure page to use it on.
 *
 * Not narrowed by the frame. A page embedded in another one can only reach the
 * camera if the host lends it with `allow="camera"`, and the preview frame does
 * not — but taking the button away there would take it away from the counter
 * too, which opens this app in a tab of its own, where the camera is exactly
 * what somebody photographing a member wants. So it stays, and a refusal says
 * which of the two happened.
 */
const cameraAvailable = () =>
  typeof navigator !== "undefined" &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  window.isSecureContext;

/**
 * What to call a photograph taken here.
 *
 * A capture is not a file and has no name of its own, so the activity log would
 * otherwise show the key it was stored under — a line of hexadecimal that says
 * nothing about where the picture came from. The date and time say both: this
 * was taken on the spot, and when.
 *
 * `2026-09-24 10.48` rather than `10:48`: a colon cannot be in a filename on
 * Windows, and this is a name people will copy into places that mind.
 */
export const capturedName = (at: Date = new Date()) => {
  const pad = (value: number) => String(value).padStart(2, "0");
  const date = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  return `Camera photo ${date} ${pad(at.getHours())}.${pad(at.getMinutes())}.jpg`;
};

/**
 * A profile picture: the picture itself, and everything else behind it.
 *
 * Two buttons sat beside the picture and read as a form control rather than a
 * picture you can change. The whole picture is the button now, and a camera
 * comes up over it while it is pointed at — legacy's own gesture. Pressing it
 * opens the choice — a file, or the camera — and either way the image lands in
 * a crop stage first, so what is saved is a square someone framed rather than
 * whatever shape the file was.
 *
 * Nothing large is ever sent: the crop is drawn to a 160px canvas here, so a
 * 12-megapixel photo leaves as a few kilobytes of JPEG.
 */
export function AvatarPicker({
  src,
  name,
  onChange,
  onRemove,
  busy = false,
  className,
  editable = true,
  placeholder = "initials",
  title,
}: {
  src: string | null | undefined;
  name: string;
  /** The picture, and what it was called where it came from. */
  onChange: (dataUrl: string, fileName?: string) => void | Promise<void>;
  onRemove?: (() => void) | undefined;
  busy?: boolean;
  className?: string | undefined;
  /** False shows the picture and nothing else — a record you may not change. */
  editable?: boolean;
  /** What stands in for a missing picture; see `Avatar`. */
  placeholder?: "initials" | "person";
  /** The chooser's heading, for a screen that names the picture its own way. */
  title?: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const avatar = (
    <Avatar
      src={src}
      name={name}
      placeholder={placeholder}
      className={cn("size-24 rounded-2xl text-3xl", className)}
    />
  );

  if (!editable) return avatar;

  // The overlay is the picture's own shape: whatever rounding the caller gave
  // the avatar, the sheet over it takes as well — a round photograph under a
  // square shade is two shapes where there is one thing.
  const radius = className?.match(/rounded-\S+/g)?.join(" ") ?? "rounded-2xl";

  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen(true)}
        className={cn("group relative shrink-0 disabled:opacity-60", radius)}
        title="Change picture"
      >
        {avatar}
        {/* On the picture, and only while it is pointed at, as legacy had it
            (`mrm-detail-banner__avatar-camera`). A badge pinned to the corner
            sat where the member's own marks sit — the volume-shopper cart is
            in that corner — and said "you may change this" at all times, about
            the one thing on the page nobody came to change. */}
        <span
          className={cn(
            "bg-ink-900/45 pointer-events-none absolute inset-0 grid place-items-center text-white",
            "opacity-0 transition-opacity duration-[var(--duration-fast)]",
            "group-hover:opacity-100 group-focus-visible:opacity-100",
            radius,
          )}
        >
          <Camera className="size-7" aria-hidden />
        </span>
        <span className="sr-only">Change picture</span>
      </button>

      {open ? (
        <PictureChooser
          current={src}
          {...(title ? { title } : {})}
          onClose={() => setOpen(false)}
          onRemove={
            onRemove
              ? () => {
                  setOpen(false);
                  onRemove();
                }
              : undefined
          }
          onDone={(dataUrl, fileName) => {
            setOpen(false);
            void onChange(dataUrl, fileName);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The picture on file, and the two ways to replace it — one well, staged.
 *
 * Ported from `legacy/frontendx/src/apps/mrm/components/MrmRowDetailView/
 * MrmProfileImageModal.tsx` and the SCSS beside it: a 240px round well with the
 * picture filling it, the delete standing on the picture while it is pointed
 * at, and "Upload Profile Image" with a camera beside it when there is none.
 *
 * Staged, which is the point of the shape. Nothing here touches the record: the
 * delete empties the well, a chosen file lands in it through the crop stage,
 * and only Save hands the result to the caller. Removal used to fire the moment
 * the button was pressed, so a misclick was a picture gone with nothing to
 * cancel — the sheet said "Remove picture" and meant it immediately.
 *
 * Exported because the trigger is not always the picture. The profile sheet
 * puts the picture and the pencil beside each other and gives them different
 * jobs — the picture shows you what is on file, the pencil replaces it — so it
 * opens this itself rather than going through `AvatarPicker`'s own button.
 * Mount it only while it is open; it keeps its stage in local state.
 */
export function PictureChooser({
  current,
  initialSource = null,
  initialSourceName = null,
  title = "Change picture",
  onDone,
  onRemove,
  onClose,
}: {
  /** The picture on file, shown as it stands before anything replaces it. */
  current?: string | null | undefined;
  /**
   * A file already chosen elsewhere — dropped onto the well, say — so the
   * dialog opens on the crop stage rather than asking again for the file the
   * person has just handed it.
   */
  initialSource?: string | null | undefined;
  /** What that file was called, where the caller knows it. */
  initialSourceName?: string | null | undefined;
  title?: string;
  onDone: (dataUrl: string, fileName?: string) => void;
  /** Absent means the picture may be replaced but not simply taken away. */
  onRemove?: (() => void) | undefined;
  onClose: () => void;
}) {
  /**
   * What the well holds, against what is on file.
   *
   * `undefined` is "nobody has touched it" — which is not the same answer as
   * `null`, "emptied" — and the difference is what Save is lit by. Without it,
   * opening the sheet on a member with no picture and closing it again would
   * read as a removal.
   */
  const [staged, setStaged] = useState<string | null | undefined>(undefined);
  const [source, setSource] = useState<string | null>(initialSource);
  /**
   * What to call the picture in the well.
   *
   * A file brings its own name; a photograph taken here has none, so it is
   * given one. It travels with the picture because the crop re-encodes it —
   * what is saved is a JPEG this made, and the only thing left that says where
   * it came from is the name. The member's activity log is where it shows.
   */
  const [sourceName, setSourceName] = useState<string | null>(initialSourceName);
  const [camera, setCamera] = useState(false);
  const [over, setOver] = useState(false);
  /** The picture at a size somebody can actually look at, over this sheet. */
  const [preview, setPreview] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const shown = staged === undefined ? (current ?? null) : staged;
  const changed = staged !== undefined && staged !== (current ?? null);
  // A picture is always saveable. An empty well is a removal, and a caller that
  // does not take one has nothing to be handed — legacy simply held Save shut
  // on a member with no image, which is the same button in the same state.
  const saveable = changed && (shown !== null || Boolean(onRemove));

  const readFile = (chosen: File | undefined) => {
    if (!chosen) return;
    if (!chosen.type.startsWith("image/")) {
      toast.error("That file is not an image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSource(String(reader.result));
      setSourceName(chosen.name);
    };
    reader.onerror = () => toast.error("That file could not be read.");
    reader.readAsDataURL(chosen);
  };
  const browse = () => file.current?.click();

  if (source) {
    return (
      <CropStage
        source={source}
        // Back to wherever the picture came from. A capture leaves the lens
        // open behind the crop on purpose: a photograph that framed badly is
        // usually a photograph to take again, not a file to go and find.
        onBack={() => setSource(null)}
        // Into the well, not onto the record: the crop is a step on the way to
        // Save, so what it produces is looked at before it is committed.
        //
        // The lens is closed here rather than when it hands the frame over —
        // leaving it open was what sent an accepted crop straight back to the
        // camera: `source` cleared, `camera` did not, and the stage below
        // caught it on the way out.
        onDone={(dataUrl) => {
          setStaged(dataUrl);
          setSource(null);
          setCamera(false);
        }}
      />
    );
  }
  if (camera) {
    return (
      <CameraStage
        onClose={() => setCamera(false)}
        onCapture={(dataUrl) => {
          setSource(dataUrl);
          setSourceName(capturedName());
        }}
      />
    );
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="sm"
        title={title}
        footer={
          <>
            <Button variant="secondary" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              disabled={!saveable}
              onClick={() => {
                if (!saveable) return;
                if (shown === null) onRemove?.();
                else onDone(shown, sourceName ?? undefined);
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="grid justify-items-center gap-3 py-2">
          {/* Legacy's 240px circle. A click anywhere in it opens the picker while
            it is empty, which is what makes a target of it; once there is a
            picture the only thing standing on it is the way to take it off, so
            the well stops taking clicks and the delete carries them. */}
          <div
            onClick={shown ? undefined : browse}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setOver(false);
              readFile(event.dataTransfer.files[0]);
            }}
            className={cn(
              "group/well border-line-strong bg-surface-raised relative grid size-60",
              "place-items-center overflow-hidden rounded-full border text-center",
              !shown && "cursor-pointer",
              over && "border-accent bg-accent-soft/30",
            )}
          >
            {shown ? (
              <>
                {/* The picture is its own button: a 240px circle answers "which
                  photograph is on file" only roughly, and the face is the whole
                  reason anybody opened this. A real button rather than a click
                  handler on the well, so it is reachable from the keyboard and
                  the delete standing on it is a sibling rather than a press
                  inside a press. */}
                <button
                  type="button"
                  onClick={() => setPreview(true)}
                  className="size-full cursor-zoom-in"
                >
                  <img src={shown} alt="" className="size-full object-cover" />
                  <span className="sr-only">View picture</span>
                </button>
                {/* The house's delete-over-a-picture: centred, and only while the
                  picture is pointed at or the button itself has the keyboard. */}
                <button
                  type="button"
                  onClick={() => setStaged(null)}
                  className={cn(
                    "absolute top-1/2 left-1/2 z-10 grid size-9 -translate-x-1/2 -translate-y-1/2",
                    "place-items-center rounded-full bg-black/55 text-white opacity-0",
                    "transition-opacity duration-[var(--duration-base)] hover:bg-black/75",
                    "group-hover/well:opacity-100 focus-visible:opacity-100",
                  )}
                >
                  <Trash2 className="size-4" aria-hidden />
                  <span className="sr-only">Remove picture</span>
                </button>
              </>
            ) : (
              <div className="grid justify-items-center gap-3 px-8">
                {/* The well takes a click anywhere; this is the same act with a
                  place on the keyboard, since it is the only way to a file. */}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    browse();
                  }}
                  className="text-fg-muted text-callout grid justify-items-center gap-1.5"
                >
                  <Upload className="text-fg-subtle size-6" aria-hidden />
                  <span>Upload Profile Image</span>
                </button>
                {/* The other way to get a picture, on the same panel as the
                  first — the counter has the person in front of it either way. */}
                {cameraAvailable() ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setCamera(true);
                    }}
                    className={cn(
                      "border-accent text-accent hover:bg-accent-soft/40 grid size-10",
                      "place-items-center rounded-full border-2",
                    )}
                  >
                    <Camera className="size-5" aria-hidden />
                    <span className="sr-only">Take a photo</span>
                  </button>
                ) : null}
              </div>
            )}
          </div>
          {/* Driven by the well around it rather than reached on its own, so it
            is out of the tab order and the accessibility tree. */}
          <input
            ref={file}
            type="file"
            accept="image/*"
            tabIndex={-1}
            aria-hidden
            className="sr-only"
            onChange={(event) => {
              readFile(event.target.files?.[0]);
              // Cleared so that picking the same file twice is two picks.
              event.target.value = "";
            }}
          />
        </div>
      </Modal>

      {/* Over the sheet rather than in place of it, so closing the picture puts
          the person back where they were with the framing still staged. What is
          shown is the well's own picture — the one that would be saved, not the
          one on record, which after a crop are two different photographs, and
          so not a file with a name to download either. */}
      {preview && shown ? (
        <FilePreview src={shown} title={title} size="md" onClose={() => setPreview(false)} />
      ) : null}
    </>
  );
}

/**
 * Frame the picture: drag to move, slide to zoom.
 *
 * The preview and the export share one transform — a scale and an offset in
 * stage pixels — so what is saved is exactly what was framed, scaled down by
 * the ratio between the stage and the stored size. The offset is clamped so
 * the square is always covered; there is no way to frame an empty corner.
 */
function CropStage({
  source,
  onDone,
  onBack,
}: {
  source: string;
  onDone: (dataUrl: string) => void;
  onBack: () => void;
}) {
  const image = useRef<HTMLImageElement>(null);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);

  // "Cover" is the floor: at zoom 1 the shorter side exactly fills the stage.
  const base = natural ? Math.max(STAGE / natural.width, STAGE / natural.height) : 1;
  const scale = base * zoom;
  const width = (natural?.width ?? 0) * scale;
  const height = (natural?.height ?? 0) * scale;

  const clamp = (next: { x: number; y: number }) => ({
    x: Math.min(0, Math.max(STAGE - width, next.x)),
    y: Math.min(0, Math.max(STAGE - height, next.y)),
  });

  // Re-centre whenever the zoom changes, keeping the middle of the stage put.
  useEffect(() => {
    setOffset((current) => ({
      x: Math.min(0, Math.max(STAGE - width, current.x)),
      y: Math.min(0, Math.max(STAGE - height, current.y)),
    }));
  }, [width, height]);

  const onLoad = () => {
    const element = image.current;
    if (!element) return;
    const cover = Math.max(STAGE / element.naturalWidth, STAGE / element.naturalHeight);
    setNatural({ width: element.naturalWidth, height: element.naturalHeight });
    setOffset({
      x: (STAGE - element.naturalWidth * cover) / 2,
      y: (STAGE - element.naturalHeight * cover) / 2,
    });
  };

  const save = () => {
    const element = image.current;
    if (!element || !natural) return;
    /**
     * How big the framed square actually is in the source, capped and floored.
     *
     * Exporting at a fixed `MAX_SIZE` would upscale a small file into a large
     * blurry JPEG — more bytes, no more picture. `STAGE / scale` is the side of
     * the crop measured in the source's own pixels, which is the most detail
     * there is to keep.
     */
    const side = Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.round(STAGE / scale)));
    const render = (fraction: number, quality: number) => {
      const size = Math.max(1, Math.round(side * fraction));
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      // The stage, rescaled: same framing, stored size.
      const ratio = size / STAGE;
      canvas
        .getContext("2d")
        ?.drawImage(element, offset.x * ratio, offset.y * ratio, width * ratio, height * ratio);
      return canvas.toDataURL("image/jpeg", quality);
    };
    // A dense photograph can still be too long at full quality, so it is
    // re-encoded smaller until it fits rather than being refused by the API.
    let picture = "";
    for (const [fraction, quality] of RENDERINGS) {
      picture = render(fraction, quality);
      if (picture.length <= PICTURE_BUDGET) break;
    }
    onDone(picture);
  };

  return (
    <Modal
      open
      onClose={onBack}
      size="sm"
      title="Frame the picture"
      description="Drag to move it, and use the slider to zoom."
      footer={
        <>
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
          {/* Not "Save": this hands the framed square to the well behind it,
              and the well's own Save is what reaches the record. */}
          <Button disabled={!natural} onClick={save}>
            Use this photo
          </Button>
        </>
      }
    >
      <div className="grid justify-items-center gap-4 pb-2">
        <div
          className="bg-surface-inset relative touch-none overflow-hidden rounded-2xl"
          style={{ width: STAGE, height: STAGE }}
          onPointerDown={(event) => {
            drag.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!drag.current) return;
            setOffset(
              clamp({ x: event.clientX - drag.current.x, y: event.clientY - drag.current.y }),
            );
          }}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
        >
          <img
            ref={image}
            src={source}
            alt=""
            draggable={false}
            onLoad={onLoad}
            className="max-w-none cursor-grab select-none active:cursor-grabbing"
            style={{
              width: width || undefined,
              height: height || undefined,
              transform: `translate(${offset.x}px, ${offset.y}px)`,
            }}
          />
        </div>
        <label className="flex w-full max-w-72 items-center gap-3">
          <span className="text-fg-muted text-subhead">Zoom</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            className="accent-accent w-full"
          />
        </label>
      </div>
    </Modal>
  );
}

/**
 * The camera, for as long as this is on screen and not a moment longer.
 *
 * The stream is stopped in the effect's cleanup, so closing the dialog — by
 * button, by Escape, or by the component going away — turns the light off. A
 * page that leaves the camera running is a bug people notice once and never
 * trust again. The full frame is handed on; the crop stage does the framing.
 */
function CameraStage({
  onCapture,
  onClose,
}: {
  onCapture: (dataUrl: string) => void;
  onClose: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user", width: 1280, height: 720 } })
      .then((granted) => {
        // Opened and closed faster than the prompt was answered: stop it rather
        // than attach a stream to an element that is gone.
        if (cancelled) {
          granted.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = granted;
        if (video.current) video.current.srcObject = granted;
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        const refused = cause instanceof DOMException && cause.name === "NotAllowedError";
        setError(
          refused && inAFrame()
            ? "This page is embedded, and the camera belongs to the page around it. Upload a photo instead."
            : refused
              ? "The camera was not allowed. Check the site's permissions and try again."
              : "No camera could be started on this device.",
        );
      });
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const take = () => {
    const element = video.current;
    // A frame, not merely a stream. Capture is shut until there is one, and
    // this is the same rule said again for the keyboard and for a lens that
    // drops out between the two.
    if (!element || !element.videoWidth || !element.videoHeight) return;
    const canvas = document.createElement("canvas");
    canvas.width = element.videoWidth;
    canvas.height = element.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    // Taken as it was previewed: the mirror is undone here, not left to
    // surprise someone when the saved picture faces the other way.
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(element, 0, 0);
    onCapture(canvas.toDataURL("image/jpeg", 0.92));
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Take a photo"
      description="You will be able to crop it next."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button disabled={!ready} onClick={take}>
            <Camera aria-hidden /> Capture
          </Button>
        </>
      }
    >
      {error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger text-callout rounded-lg px-3 py-2.5"
        >
          {error}
        </p>
      ) : (
        <div className="bg-surface-inset aspect-square overflow-hidden rounded-xl">
          <video
            ref={video}
            autoPlay
            playsInline
            muted
            /**
             * Ready is a frame, not a stream.
             *
             * This used to light Capture the moment `srcObject` was assigned,
             * which is a beat or two before the device has decoded anything:
             * pressing it then reads `videoWidth` of 0, draws a 0x0 canvas and
             * hands the crop stage a data URL no `<img>` can load — so the
             * framing never arms and Use this photo stays shut, with nothing
             * on screen to say why. `loadedmetadata` is where the dimensions
             * arrive.
             */
            onLoadedMetadata={(event) => setReady(event.currentTarget.videoWidth > 0)}
            // Mirrored, because a preview of your own face that moves the wrong
            // way is the one thing everybody finds unusable.
            className="size-full scale-x-[-1] object-cover"
          />
        </div>
      )}
    </Modal>
  );
}
